// ============================================================================
// ACTIVESPRINTPANEL.JS - PANEL SIMPLIFICADO CON DRAG & DROP ARREGLADO
// CORREGIDO: Funcionalidades de movimiento y reordenamiento funcionando
// ============================================================================

import React, { useState, useMemo, useEffect, useRef, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { m, AnimatePresence, useReducedMotion } from 'framer-motion';
import {
  Archive,
  Clock,
  ChevronsUp,
  ChevronsDown,
  ChevronDown,
  Check,
  Equal,
  GripVertical,
  MoreHorizontal,
  Pencil,
  Link2,
  Repeat,
  Trash2,
  Search,
  Plus,
  User,
  ArrowUp,
  ArrowDown,
  ArrowUpToLine,
  ArrowDownToLine,
  Ban,
  MessageSquarePlus,
  RotateCcw,
  X,
  Save,
  Calendar,
  CalendarClock,
  CalendarX,
  Play,
} from 'lucide-react';
import { useTaskStore } from '../store/TaskStore';
import { shallow } from 'zustand/shallow';
import appConfig from '../config/appConfig';
import { useAuth } from '../auth/AuthContext';
import useToast from '../services/useToast';
import { getEpicColor } from '../utils/colorUtils';
import { getTaskRef, getTaskPath } from '../utils/taskRef';
import { getDueDateStatus, getDueDateStatusLabel, formatDueDate } from '../utils/dueDate';
import FilterMenu from './FilterMenu';
import Avatar from './Avatar';
import AssigneePicker from './AssigneePicker';
import SprintFilterRings from './SprintFilterRings';
import CloseSprintModal from './CloseSprintModal';
import CreateSprintModal from './CreateSprintModal';

// Iniciales para el avatar de un equipo responsable (SEO→SEO, Front→FR, "Equipo Front"→EF)
function getTeamInitials(name) {
  if (!name) return '?';
  const words = name.trim().split(/\s+/);
  if (words.length >= 2) {
    return words.slice(0, 3).map(w => w[0]).join('').toUpperCase();
  }
  return name.length <= 3 ? name.toUpperCase() : name.slice(0, 2).toUpperCase();
}

// ============================================================================
// HELPERS PUROS (nivel de módulo: no se recrean por render)
// ============================================================================

const formatDate = (dateString) => {
  if (!dateString) return 'Sin fecha';

  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  } catch (error) {
    return 'Fecha inválida';
  }
};

const getPriorityIcon = (prioridad) => {
  switch (prioridad) {
    case 'alta': return <ChevronsUp size={16} />;
    case 'media': return <Equal size={16} />;
    case 'baja': return <ChevronsDown size={16} />;
    default: return <Equal size={16} />;
  }
};

const getPriorityLabel = (prioridad) => {
  switch (prioridad) {
    case 'alta': return 'Prioridad alta';
    case 'media': return 'Prioridad media';
    case 'baja': return 'Prioridad baja';
    default: return 'Sin prioridad';
  }
};

const getStatusLabel = (estado) => {
  switch (estado) {
    case 'completada': return 'Completada';
    case 'en-curso': return 'En curso';
    case 'pendiente': return 'Pendiente';
    default: return estado;
  }
};

const STATUS_OPTIONS = ['pendiente', 'en-curso', 'completada'];

// En teléfono los menús largos de fila (kebab, épica, grupo) se abren como hoja
// inferior (.u-sheet) con lista desplazable que no depende de la posición del
// botón: en teléfono ningún scroll los cierra. En el resto de anchos un scroll
// dentro del menú tampoco; cualquier otro scroll de la página sí (popover anclado).
const isScrollInsideMenu = (e) =>
  e?.type === 'scroll' && (
    (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 480px)').matches)
    || (e.target instanceof Element && !!e.target.closest('.task-menu'))
  );

const getSEOIndicator = (validadoPorSEO) => (
  <div
    className={`seo-indicator ${validadoPorSEO ? 'seo-validated' : 'seo-not-validated'}`}
    title={validadoPorSEO ? 'Validada por SEO' : 'No validada'}
  >
    <div className="seo-circle"></div>
  </div>
);

// ============================================================================
// FILA DE TAREA MEMOIZADA
// ============================================================================
// Extraída del render del panel para cortar la cascada de re-renders: cada fila
// solo se repinta si cambia SU tarea, su menú abierto o su drag-over. Los
// handlers llegan vía `actions`, un objeto de identidad estable (ver rowActions).

const TaskRow = React.memo(function TaskRow({
  task,
  epics,
  activeEpics,
  responsables,
  reduceMotion,
  isDragOver,
  isDeleting,
  dragKind,
  showMoveButton,
  hasActiveSprint,
  planningSprintId,
  isAdminFlag,
  menuState,
  menuLayout,
  menuRef,
  statusMenuState,
  epicMenuState,
  responsableMenuState,
  actions,
}) {
  const canDrag = dragKind !== 'none' && !task.validadoPorSEO;
  const dragStart = dragKind === 'backlog' ? actions.backlogDragStart : actions.sprintDragStart;
  const dragOver = dragKind === 'backlog' ? actions.backlogDragOver : actions.sprintDragOver;
  const dragLeave = dragKind === 'backlog' ? actions.backlogDragLeave : actions.sprintDragLeave;
  const drop = dragKind === 'backlog' ? actions.backlogDrop : actions.sprintDrop;

  return (
    <m.tr
      data-motion-fallback
      layout={reduceMotion ? false : 'position'}
      initial={reduceMotion ? false : { opacity: 0, y: -4 }}
      animate={reduceMotion ? {} : { opacity: 1, y: 0 }}
      exit={reduceMotion ? {} : { opacity: 0, y: 4 }}
      transition={{ duration: 0.18, ease: 'easeOut' }}
      className={`task-row clickable ${isDragOver ? 'drag-over' : ''} ${task.validadoPorSEO ? 'seo-locked' : ''} ${task.bloqueada ? 'is-blocked' : ''}`}
      draggable={canDrag}
      onDragStart={(e) => canDrag && dragStart(e, task)}
      onDragOver={(e) => canDrag && dragOver(e, task)}
      onDragLeave={dragLeave}
      onDrop={(e) => canDrag && drop(e, task)}
      onClick={() => actions.taskClick(task)}
    >
      <td className="col-drag">
        <GripVertical
          size={14}
          className={`drag-handle ${task.validadoPorSEO ? 'drag-disabled' : ''}`}
        />
      </td>

      <td className="col-id">
        <span className={`task-id-cell ${task.estado === 'completada' ? 'done' : ''}`}>
          {getTaskRef(task)}
        </span>
      </td>

      <td className="col-name">
        <div className="task-name-container">
          <span className="task-name">{task.nombre}</span>
          <div className="task-name-meta">
            {!!task.bloqueada && (
              <span className="task-blocked-lozenge" title="Tarea bloqueada">
                <Ban size={11} />
                Bloqueada
              </span>
            )}
            {(() => {
              const dueStatus = getDueDateStatus(task.fecha_vencimiento, task.estado);
              if (dueStatus === 'none') return null;
              const DueIcon = dueStatus === 'overdue'
                ? CalendarX
                : dueStatus === 'today'
                  ? CalendarClock
                  : Calendar;
              const statusLabel = getDueDateStatusLabel(dueStatus);
              const dateLabel = formatDueDate(task.fecha_vencimiento);
              return (
                <span
                  className={`task-duedate-chip is-${dueStatus}`}
                  title={statusLabel ? `${statusLabel} · vence el ${dateLabel}` : `Vence el ${dateLabel}`}
                >
                  <DueIcon size={11} />
                  {statusLabel || dateLabel}
                </span>
              );
            })()}
          </div>
        </div>
      </td>

      <td className="col-epic" onClick={(e) => e.stopPropagation()}>
        <span
          className="task-epic"
          style={{ ...getEpicColor(task.epica, epics), cursor: 'pointer' }}
          title="Cambiar épica"
          onClick={(e) => actions.epicMenuToggle(e, task)}
        >
          {task.epica || '-'}
        </span>

        {epicMenuState && <div className="task-menu-scrim" aria-hidden="true" onClick={actions.closeMenus} />}
        {epicMenuState && (
          <div
            className="task-menu status-menu u-sheet"
            style={{ top: epicMenuState.top, left: epicMenuState.left }}
          >
            <div className="task-menu-sheet-head">
              <span className="task-menu-sheet-title">Épica</span>
              <button type="button" className="task-menu-sheet-close" onClick={actions.closeMenus} aria-label="Cerrar">
                <X size={20} />
              </button>
            </div>
            {activeEpics.map((epic) => (
              <button
                key={epic.id || epic.nombre}
                className="task-menu-item"
                onClick={() => actions.quickEpicChange(task, epic.nombre)}
              >
                <span
                  className="task-epic"
                  style={getEpicColor(epic.nombre, epics)}
                >
                  {epic.nombre}
                </span>
                {(task.epica || '') === epic.nombre && <Check size={13} className="status-current-check" />}
              </button>
            ))}
            <button
              className="task-menu-item"
              onClick={() => actions.quickEpicChange(task, '')}
            >
              Sin épica
              {!task.epica && <Check size={13} className="status-current-check" />}
            </button>
          </div>
        )}
      </td>

      <td className="col-priority">
        <span
          className={`priority-badge priority-${task.prioridad}`}
          title={getPriorityLabel(task.prioridad)}
        >
          {getPriorityIcon(task.prioridad)}
        </span>
      </td>

      <td className="col-status" onClick={(e) => e.stopPropagation()}>
        <button
          className={`status-badge status-${task.estado} status-badge-btn`}
          title="Cambiar estado"
          onClick={(e) => actions.statusMenuToggle(e, task)}
        >
          {getStatusLabel(task.estado)}
          <ChevronDown size={11} />
        </button>

        {statusMenuState && (
          <div
            className="task-menu status-menu"
            style={{ top: statusMenuState.top, left: statusMenuState.left }}
          >
            {STATUS_OPTIONS.map((estado) => (
              <button
                key={estado}
                className="task-menu-item"
                onClick={() => actions.quickStatusChange(task, estado)}
              >
                <span className={`status-dot status-dot-${estado}`}></span>
                {getStatusLabel(estado)}
                {task.estado === estado && <Check size={13} className="status-current-check" />}
              </button>
            ))}
          </div>
        )}
      </td>

      <td className="col-seo">
        {getSEOIndicator(task.validadoPorSEO)}
      </td>

      <td className="col-responsable" onClick={(e) => e.stopPropagation()}>
        <span
          className="task-responsable"
          style={{ cursor: 'pointer' }}
          title="Cambiar grupo asignado"
          onClick={(e) => actions.responsableMenuToggle(e, task)}
        >
          {task.responsable || 'Sin asignar'}
        </span>

        {responsableMenuState && <div className="task-menu-scrim" aria-hidden="true" onClick={actions.closeMenus} />}
        {responsableMenuState && (
          <div
            className="task-menu status-menu u-sheet"
            style={{ top: responsableMenuState.top, left: responsableMenuState.left }}
          >
            <div className="task-menu-sheet-head">
              <span className="task-menu-sheet-title">Responsable</span>
              <button type="button" className="task-menu-sheet-close" onClick={actions.closeMenus} aria-label="Cerrar">
                <X size={20} />
              </button>
            </div>
            {responsables.map((responsable) => (
              <button
                key={responsable}
                className="task-menu-item"
                onClick={() => actions.quickResponsableChange(task, responsable)}
              >
                {responsable}
                {(task.responsable || '') === responsable && <Check size={13} className="status-current-check" />}
              </button>
            ))}
            <button
              className="task-menu-item"
              onClick={() => actions.quickResponsableChange(task, '')}
            >
              Sin asignar
              {!task.responsable && <Check size={13} className="status-current-check" />}
            </button>
          </div>
        )}
      </td>

      <td className="col-asignado" onClick={(e) => e.stopPropagation()}>
        <AssigneePicker
          value={task.assigned_to}
          assigneeName={task.asignado_nombre}
          assigneeAvatar={task.asignado_avatar}
          size={26}
          align="right"
          onChange={(uid) => actions.assign(task.id, uid)}
        />
      </td>

      <td className="col-menu" onClick={(e) => e.stopPropagation()}>
        <button
          className={`menu-trigger ${menuState ? 'active' : ''}`}
          title="Más opciones"
          onClick={(e) => actions.menuToggle(e, task)}
        >
          {isDeleting ? (
            <div className="loading-spinner-small"></div>
          ) : (
            <MoreHorizontal size={16} />
          )}
        </button>

        {menuState && <div className="task-menu-scrim" aria-hidden="true" onClick={actions.closeMenus} />}
        {menuState && (
          <div
            ref={menuRef}
            className="task-menu u-sheet"
            style={{
              top: menuLayout?.top ?? menuState.top,
              right: menuLayout?.right ?? menuState.right,
              ...(menuLayout?.maxHeight
                ? { maxHeight: menuLayout.maxHeight, overflowY: 'auto' }
                : {}),
            }}
          >
            <div className="task-menu-sheet-head">
              <span className="task-menu-sheet-title">Más opciones</span>
              <button type="button" className="task-menu-sheet-close" onClick={actions.closeMenus} aria-label="Cerrar">
                <X size={20} />
              </button>
            </div>
            <div className="task-menu-info">
              <div className="task-menu-info-row">
                <Clock size={13} />
                <span>Modificado</span>
                <span className="task-menu-info-value">
                  {formatDate(task.fechaUltimaModificacion || task.updated_at || task.fechaModificacion)}
                </span>
              </div>
              <div className="task-menu-info-row">
                <Repeat size={13} />
                <span>Reps</span>
                <span className="task-menu-info-value">
                  {task.vecesCompletada || task.vecesRealizada || 0}
                </span>
              </div>
            </div>

            <div className="task-menu-divider"></div>

            <button
              className="task-menu-item"
              onClick={() => actions.editTask(task.id)}
            >
              <Pencil size={14} />
              Editar
            </button>

            <button
              className="task-menu-item"
              onClick={() => actions.copyLink(task)}
            >
              <Link2 size={14} />
              Copiar enlace
            </button>

            <div className="task-menu-divider"></div>
            <div className="task-menu-label">Enviar a</div>

            {hasActiveSprint && (
              <>
                <button
                  className="task-menu-item"
                  onClick={() => actions.sendTaskTo(task, 'sprint', 'start')}
                >
                  <ArrowUpToLine size={14} />
                  Inicio del sprint
                </button>
                <button
                  className="task-menu-item"
                  onClick={() => actions.sendTaskTo(task, 'sprint', 'end')}
                >
                  <ArrowDownToLine size={14} />
                  Final del sprint
                </button>
              </>
            )}

            <button
              className="task-menu-item"
              onClick={() => actions.sendTaskTo(task, 'backlog', 'start')}
            >
              <ArrowUpToLine size={14} />
              Inicio del backlog
            </button>
            <button
              className="task-menu-item"
              onClick={() => actions.sendTaskTo(task, 'backlog', 'end')}
            >
              <ArrowDownToLine size={14} />
              Final del backlog
            </button>

            {planningSprintId && task.sprintActual !== planningSprintId && (
              <button
                className="task-menu-item"
                onClick={() => actions.moveToPlanning(task.id)}
              >
                <Calendar size={14} />
                Sprint de planificación
              </button>
            )}

            <div className="task-menu-divider"></div>

            <button
              className="task-menu-item"
              onClick={() => actions.toggleBlock(task)}
            >
              <Ban size={14} />
              {task.bloqueada ? 'Quitar bloqueo' : 'Añadir bloqueo'}
            </button>
            <button
              className="task-menu-item"
              onClick={() => actions.blockAndComment(task)}
            >
              <MessageSquarePlus size={14} />
              Añadir bloqueo y comentario
            </button>

            {showMoveButton && hasActiveSprint && (
              <button
                className="task-menu-item"
                onClick={() => actions.moveToSprint(task.id)}
              >
                <ArrowUp size={14} />
                Mover al sprint
              </button>
            )}

            {!showMoveButton && (
              <button
                className="task-menu-item"
                onClick={() => actions.moveToBacklog(task.id)}
              >
                <ArrowDown size={14} />
                Mover al backlog
              </button>
            )}

            {isAdminFlag && (
              <>
                <div className="task-menu-divider"></div>
                <button
                  className="task-menu-item danger"
                  onClick={() => actions.deleteTask(task)}
                  disabled={isDeleting}
                >
                  <Trash2 size={14} />
                  Eliminar
                </button>
              </>
            )}
          </div>
        )}
      </td>
    </m.tr>
  );
});

function ActiveSprintPanel({ onTaskSelect, onCreateTask }) {
  const navigate = useNavigate();
  
  const {
    activeSprint,
    planningSprint,
    tasks,
    closeSprint,
    createSprint,
    createPlanningSprint,
    startSprint,
    deleteSprint,
    updateTask,
    moveTaskToSprint,
    sendTaskToPosition,
    reorderTasks,
    removeTaskLocal,
    restoreTaskLocal,
    commitDeleteTask,
    isLoading,
    epics,
    sprintStatusFilter: heroStatusFilter,
    setSprintStatusFilter,
    searchTerm: globalSearchTerm
  } = useTaskStore(
    (s) => ({
      activeSprint: s.activeSprint,
      planningSprint: s.planningSprint,
      tasks: s.tasks,
      closeSprint: s.closeSprint,
      createSprint: s.createSprint,
      createPlanningSprint: s.createPlanningSprint,
      startSprint: s.startSprint,
      deleteSprint: s.deleteSprint,
      updateTask: s.updateTask,
      moveTaskToSprint: s.moveTaskToSprint,
      sendTaskToPosition: s.sendTaskToPosition,
      reorderTasks: s.reorderTasks,
      removeTaskLocal: s.removeTaskLocal,
      restoreTaskLocal: s.restoreTaskLocal,
      commitDeleteTask: s.commitDeleteTask,
      isLoading: s.isLoading,
      epics: s.epics,
      sprintStatusFilter: s.sprintStatusFilter,
      setSprintStatusFilter: s.setSprintStatusFilter,
      searchTerm: s.searchTerm
    }),
    shallow
  );

  // ¿La tarea casa con un término de búsqueda? (nombre, descripción o ref CERTI-123)
  const taskMatchesTerm = (task, term) => {
    if (!term) return true;
    const t = term.toLowerCase();
    return task.nombre.toLowerCase().includes(t) ||
      (task.descripcion && task.descripcion.toLowerCase().includes(t)) ||
      getTaskRef(task).toLowerCase().includes(t);
  };

  const { isAdmin, token } = useAuth();
  const { showSuccess, showError, showWarning, showUndo, confirm } = useToast();

  // Respeta prefers-reduced-motion: si el usuario lo pide, sin animaciones de fila.
  const reduceMotion = useReducedMotion();

  // Borrados diferidos pendientes: taskId -> { timeoutId, task }. Cada borrado
  // quita la fila al instante y confirma en backend a los 6s salvo "Deshacer".
  const pendingDeletesRef = useRef(new Map());

  // Estados locales para filtros del SPRINT (arrays = multi-select, [] = todos)
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState([]);
  const [responsableFilter, setResponsableFilter] = useState([]);
  // Filtro rápido por persona asignada. Ids de usuario; 0 = "Sin asignar".
  const [asignadoFilter, setAsignadoFilter] = useState([]);
  const [epicaFilter, setEpicaFilter] = useState([]);
  const [draggedTask, setDraggedTask] = useState(null);
  const [draggedOver, setDraggedOver] = useState(null);

  // Estados locales del BACKLOG (drag & drop; los filtros son los globales del sprint)
  const [backlogDraggedTask, setBacklogDraggedTask] = useState(null);
  const [backlogDraggedOver, setBacklogDraggedOver] = useState(null);

  // Estados para el modal de crear sprint. `createSprintMode` distingue crear el
  // sprint ACTIVO (cuando no hay ninguno) del sprint de PLANIFICACIÓN.
  const [showCreateSprintModal, setShowCreateSprintModal] = useState(false);
  const [createSprintMode, setCreateSprintMode] = useState('activo');

  // Modal "Terminar sprint" (reparto de tareas sin cerrar del sprint activo).
  const [showCloseSprintModal, setShowCloseSprintModal] = useState(false);

  // Estado para controlar qué tarea se está eliminando
  const [deletingTaskId, setDeletingTaskId] = useState(null);

  // Menú contextual (kebab) por fila: id de tarea + geometría del botón en viewport.
  // El alto real del menú es dinámico (varía con activeSprint / mover / admin), así
  // que la posición vertical definitiva se calcula tras montarlo (ver useLayoutEffect).
  const [menuTask, setMenuTask] = useState(null);
  const menuRef = useRef(null);
  const [menuLayout, setMenuLayout] = useState(null);

  useEffect(() => {
    if (!menuTask) return;
    const close = (e) => { if (!isScrollInsideMenu(e)) setMenuTask(null); };
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('click', close);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('click', close);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [menuTask]);

  // Flip/clamp del kebab para que quepa SIEMPRE en el viewport. Se mide el alto
  // real del menú con el ref tras montarlo y, si abriéndolo hacia abajo se saldría
  // por el borde inferior, se abre hacia arriba; si no cabe en ningún sentido
  // (menú más alto que la pantalla) se fija arriba con scroll interno. Corre en
  // useLayoutEffect → reposiciona ANTES del primer paint, sin parpadeo.
  useLayoutEffect(() => {
    if (!menuTask) {
      if (menuLayout) setMenuLayout(null);
      return;
    }
    const el = menuRef.current;
    if (!el) return;
    const MARGIN = 8;
    const vh = window.innerHeight;
    const vw = window.innerWidth;
    const height = el.offsetHeight;
    const width = el.offsetWidth;

    // Vertical: preferir abajo; si no cabe, arriba; si tampoco, clamp + max-height.
    const belowTop = menuTask.btnBottom + 6;
    let top;
    let maxHeight;
    if (belowTop + height <= vh - MARGIN) {
      top = belowTop;
    } else {
      const aboveTop = menuTask.btnTop - 6 - height;
      if (aboveTop >= MARGIN) {
        top = aboveTop;
      } else {
        maxHeight = vh - MARGIN * 2;
        top = MARGIN;
      }
    }

    // Horizontal: está anclado por la derecha (no se corta por la derecha), pero
    // se acota el borde izquierdo por si el menú fuese más ancho que el hueco.
    let right = menuTask.right;
    if (vw - right - width < MARGIN) {
      right = Math.max(MARGIN, vw - width - MARGIN);
    }

    // En teléfono el menú es hoja inferior (.u-sheet, 92dvh): sin maxHeight en
    // línea, que pisaría ese tope.
    const isPhoneSheet = window.matchMedia && window.matchMedia('(max-width: 480px)').matches;
    setMenuLayout({ top, right, maxHeight: isPhoneSheet ? undefined : maxHeight });
    // menuLayout fuera de deps a propósito: solo recalcular al cambiar de menú.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuTask]);

  const handleMenuToggle = (e, task) => {
    e.stopPropagation();
    setEpicMenu(null);
    setResponsableMenu(null);
    if (menuTask?.id === task.id) {
      setMenuTask(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    setMenuLayout(null); // fuerza re-medida del nuevo menú antes de posicionarlo
    setMenuTask({
      id: task.id,
      btnTop: rect.top,
      btnBottom: rect.bottom,
      top: rect.bottom + 6, // posición provisional (se corrige en useLayoutEffect)
      right: window.innerWidth - rect.right
    });
  };

  const handleCopyLink = async (task) => {
    setMenuTask(null);
    const url = `${window.location.origin}${getTaskPath(task)}`;
    try {
      await navigator.clipboard.writeText(url);
      showSuccess('Enlace de la tarea copiado al portapapeles');
    } catch (error) {
      showError('No se pudo copiar el enlace');
    }
  };

  // Dropdown de cambio rápido de estado desde la etiqueta de la tabla
  const [statusMenu, setStatusMenu] = useState(null);

  useEffect(() => {
    if (!statusMenu) return;
    const close = () => setStatusMenu(null);
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('click', close);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('click', close);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [statusMenu]);


  // Posición de un menú rápido (estado/épica/grupo) con clamp horizontal y
  // flip vertical: si el dropdown no cabe por abajo, se abre hacia arriba.
  const quickMenuPosition = (rect, optionCount) => {
    // .task-menu mide al menos 210 px: se acota con ese ancho + 8 px de margen
    // para que nunca sobresalga por la derecha del viewport (patrón 5).
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - 218));
    const estimatedHeight = optionCount * 33 + 14;
    const belowTop = rect.bottom + 4;
    const top = (belowTop + estimatedHeight > window.innerHeight - 8)
      ? Math.max(8, rect.top - 4 - estimatedHeight)
      : belowTop;
    return { top, left };
  };

  const handleStatusMenuToggle = (e, task) => {
    e.stopPropagation();
    setMenuTask(null);
    setEpicMenu(null);
    setResponsableMenu(null);
    if (statusMenu?.id === task.id) {
      setStatusMenu(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    setStatusMenu({ id: task.id, ...quickMenuPosition(rect, STATUS_OPTIONS.length) });
  };

  const handleQuickStatusChange = async (task, nuevoEstado) => {
    setStatusMenu(null);
    if (task.estado === nuevoEstado) return;
    const prevEstado = task.estado;
    try {
      const result = await updateTask(task.id, { estado: nuevoEstado }, token);
      if (!result.success) {
        showError('Error al cambiar el estado: ' + (result.error || 'Error desconocido'));
        return;
      }
      showUndo(
        `Estado: "${getStatusLabel(nuevoEstado)}"`,
        () => { updateTask(task.id, { estado: prevEstado }, token); }
      );
    } catch (error) {
      showError('Error al cambiar el estado: ' + error.message);
    }
  };

  // Dropdowns de cambio rápido de épica y grupo responsable desde la tabla
  // (mismo patrón que el de estado: mismo aspecto de celda, solo se hace clicable)
  const [epicMenu, setEpicMenu] = useState(null);
  const [responsableMenu, setResponsableMenu] = useState(null);

  useEffect(() => {
    if (!epicMenu && !responsableMenu) return;
    const close = (e) => {
      if (isScrollInsideMenu(e)) return;
      setEpicMenu(null);
      setResponsableMenu(null);
    };
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('click', close);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('click', close);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [epicMenu, responsableMenu]);

  const handleEpicMenuToggle = (e, task) => {
    e.stopPropagation();
    setMenuTask(null);
    setStatusMenu(null);
    setResponsableMenu(null);
    if (epicMenu?.id === task.id) {
      setEpicMenu(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    setEpicMenu({ id: task.id, ...quickMenuPosition(rect, activeEpics.length + 1) });
  };

  const handleQuickEpicChange = async (task, nuevaEpica) => {
    setEpicMenu(null);
    if ((task.epica || '') === nuevaEpica) return;
    const prevEpica = task.epica || '';
    try {
      const result = await updateTask(task.id, { epica: nuevaEpica }, token);
      if (!result.success) {
        showError('Error al cambiar la épica: ' + (result.error || 'Error desconocido'));
        return;
      }
      showUndo(
        nuevaEpica ? `Épica: "${nuevaEpica}"` : 'Épica retirada',
        () => { updateTask(task.id, { epica: prevEpica }, token); }
      );
    } catch (error) {
      showError('Error al cambiar la épica: ' + error.message);
    }
  };

  const handleResponsableMenuToggle = (e, task) => {
    e.stopPropagation();
    setMenuTask(null);
    setStatusMenu(null);
    setEpicMenu(null);
    if (responsableMenu?.id === task.id) {
      setResponsableMenu(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    setResponsableMenu({ id: task.id, ...quickMenuPosition(rect, responsablesOptions.length + 1) });
  };

  const handleQuickResponsableChange = async (task, nuevoResponsable) => {
    setResponsableMenu(null);
    if ((task.responsable || '') === nuevoResponsable) return;
    const prevResponsable = task.responsable || '';
    try {
      const result = await updateTask(task.id, { responsable: nuevoResponsable }, token);
      if (!result.success) {
        showError('Error al cambiar el grupo: ' + (result.error || 'Error desconocido'));
        return;
      }
      showUndo(
        nuevoResponsable ? `Grupo: "${nuevoResponsable}"` : 'Grupo retirado',
        () => { updateTask(task.id, { responsable: prevResponsable }, token); }
      );
    } catch (error) {
      showError('Error al cambiar el grupo: ' + error.message);
    }
  };

  // Sincronizar filtro de estado desde los anillos de la cabecera (SprintFilterRings)
  useEffect(() => {
    setStatusFilter(heroStatusFilter);
  }, [heroStatusFilter]);

  // Wrapper para sincronizar filtro local con store (bidireccional)
  const handleStatusFilterChange = (newFilter) => {
    setStatusFilter(newFilter);
    setSprintStatusFilter(newFilter);
  };

  // ============================================================================
  // ELIMINAR TAREA — PATRÓN DEFERRED-DELETE (quitar ya + Deshacer)
  // ============================================================================
  // Confirma el borrado real en backend SOLO al vencer la ventana de "Deshacer".
  const commitPendingDelete = (taskId) => {
    const pending = pendingDeletesRef.current.get(taskId);
    if (!pending) return;
    clearTimeout(pending.timeoutId);
    pendingDeletesRef.current.delete(taskId);
    commitDeleteTask(taskId, token).then(res => {
      if (!res.success) {
        // Falló en backend: devolver la fila y avisar.
        restoreTaskLocal(pending.task);
        showError('No se pudo eliminar la tarea: ' + (res.error || 'Error desconocido'));
      }
    });
  };

  // Al desmontar (o cambiar el token), ejecuta (flush) los borrados que sigan
  // pendientes para no perderlos: se confirman en backend inmediatamente.
  useEffect(() => {
    const pending = pendingDeletesRef.current;
    return () => {
      pending.forEach(({ timeoutId, task }) => {
        clearTimeout(timeoutId);
        commitDeleteTask(task.id, token);
      });
      pending.clear();
    };
  }, [commitDeleteTask, token]);

  const handleDeleteTask = (task) => {
    // Ya hay un borrado en curso para esta tarea: no re-encolar.
    if (pendingDeletesRef.current.has(task.id)) return;

    // 1) Quitar la fila al instante (optimista, sin backend).
    removeTaskLocal(task.id);

    // 2) Programar el borrado real; si se deshace, se cancela.
    const timeoutId = setTimeout(() => commitPendingDelete(task.id), 6000);
    pendingDeletesRef.current.set(task.id, { timeoutId, task });

    // 3) Toast con "Deshacer": restaura la fila y cancela el borrado.
    showUndo(
      `Tarea "${task.nombre}" eliminada`,
      () => {
        const pending = pendingDeletesRef.current.get(task.id);
        if (!pending) return;
        clearTimeout(pending.timeoutId);
        pendingDeletesRef.current.delete(task.id);
        restoreTaskLocal(pending.task);
      },
      { duration: 6000 }
    );
  };

  // ============================================================================
  // MANEJADORES DE SPRINT
  // ============================================================================

  // Abre el modal "Terminar sprint" (el reparto de las sin-cerrar se decide ahí).
  const handleCloseSprint = () => {
    if (!activeSprint) return;
    setShowCloseSprintModal(true);
  };

  // Confirmación desde el modal: cierra el sprint activo repartiendo las tareas
  // sin cerrar según el destino elegido. Devuelve {success} para que el modal
  // sepa si cerrarse o reactivar el botón.
  const handleConfirmCloseSprint = async (destino, opts) => {
    if (!activeSprint) return { success: false };
    try {
      const result = await closeSprint(activeSprint.id, { destino, ...opts }, token);
      if (result?.success) {
        setShowCloseSprintModal(false);
        const data = result.data || {};
        const movidas = data.tareas_movidas ?? 0;
        const destinoLabel = destino === 'backlog'
          ? 'al backlog'
          : destino === 'nuevo'
            ? 'a un nuevo sprint de planificación'
            : 'al sprint de planificación';
        showSuccess(
          `Sprint "${activeSprint.nombre}" terminado. ${data.cerradas ?? 0} cerradas, ${movidas} movidas ${destinoLabel}.`
        );
        return { success: true };
      }
      showError('Error al terminar el sprint: ' + (result?.error || 'Error desconocido'));
      return { success: false };
    } catch (error) {
      console.error('Unexpected error closing sprint:', error);
      showError('Error inesperado al terminar el sprint');
      return { success: false };
    }
  };

  // Inicia el sprint de planificación → activo. Solo si NO hay sprint activo
  // (invariante máx 1 activo); el botón ya sale deshabilitado en ese caso, esto
  // es la salvaguarda.
  const handleStartSprint = async () => {
    if (!planningSprint) return;
    if (activeSprint) {
      showWarning(`Termina primero el sprint activo (${activeSprint.nombre}) para iniciar este`);
      return;
    }
    try {
      const result = await startSprint(planningSprint.id, token);
      if (result?.success) {
        showSuccess(`Sprint "${planningSprint.nombre}" iniciado. ¡A trabajar!`);
      } else {
        showError('Error al iniciar el sprint: ' + (result?.error || 'Error desconocido'));
      }
    } catch (error) {
      console.error('Error starting sprint:', error);
      showError('Error inesperado al iniciar el sprint');
    }
  };

  const openCreateSprintModal = (mode) => {
    setCreateSprintMode(mode);
    setShowCreateSprintModal(true);
  };

  // Crear el sprint ACTIVO (solo disponible cuando no hay ninguno activo).
  const handleCreateSprint = () => openCreateSprintModal('activo');

  // Crear el sprint de PLANIFICACIÓN (solo disponible cuando no hay ninguno).
  const handleCreatePlanningSprint = () => openCreateSprintModal('planificacion');

  // Eliminar el sprint de planificación (p.ej. si se creó por error). Confirmación
  // de marca. El backend solo permite borrar sprints SIN tareas → si tiene tareas,
  // el mensaje lo aclara.
  const handleDeletePlanningSprint = async () => {
    if (!planningSprint) return;
    const ok = await confirm(
      `¿Eliminar el sprint de planificación "${planningSprint.nombre}"? Esta acción no se puede deshacer.`,
      { title: 'Eliminar sprint de planificación', confirmText: 'Eliminar', cancelText: 'Cancelar' }
    );
    if (!ok) return;
    try {
      const result = await deleteSprint(planningSprint.id, token);
      if (result?.success) {
        showSuccess('Sprint de planificación eliminado.');
      } else {
        showError(result?.error || 'No se pudo eliminar (¿tiene tareas asignadas? muévelas primero).');
      }
    } catch (error) {
      console.error('Error deleting planning sprint:', error);
      showError('Error al eliminar el sprint de planificación');
    }
  };

  // Recibe los datos del CreateSprintModal (que valida el nombre y aísla su
  // propio estado del formulario) y hace la llamada al store + toasts.
  const handleCreateSprintSubmit = async (sprintData) => {
    const isPlanning = createSprintMode === 'planificacion';

    try {
      const result = isPlanning
        ? await createPlanningSprint(sprintData, token)
        : await createSprint(sprintData, token);

      if (result.success) {
        const message = isPlanning
          ? `Sprint de planificación "${sprintData.nombre}" creado. Ve llenándolo; inícialo cuando termines el activo.`
          : `Sprint "${sprintData.nombre}" creado y activado. Ya puedes empezar a trabajar en él.`;

        showSuccess(message);
        setShowCreateSprintModal(false);
      } else {
        showError('Error al crear sprint: ' + result.error);
      }
    } catch (error) {
      console.error('Error creating sprint:', error);
      showError('Error inesperado al crear sprint');
    }
  };


  // ============================================================================
  // FILTROS Y DATOS COMPUTADOS
  // ============================================================================

  // Todas las tareas del sprint (sin filtrar) - para extraer opciones de filtros
  const allSprintTasks = useMemo(() => {
    if (!activeSprint) return [];
    return tasks.filter(task => task.sprintActual === activeSprint.id && task.estado !== 'borrador');
  }, [tasks, activeSprint]);

  const sprintTasks = useMemo(() => {
    if (!activeSprint) return [];

    return allSprintTasks
      .filter(task => {
        const matchesSearch = taskMatchesTerm(task, searchTerm) &&
          taskMatchesTerm(task, globalSearchTerm);

        const matchesStatus = statusFilter.length === 0 || statusFilter.includes(task.estado);
        const matchesResponsable = responsableFilter.length === 0 || responsableFilter.includes(task.responsable);
        const matchesAsignado = asignadoFilter.length === 0 || asignadoFilter.includes(task.assigned_to ? Number(task.assigned_to) : 0);
        const matchesEpica = epicaFilter.length === 0 || epicaFilter.includes(task.epica);

        return matchesSearch && matchesStatus && matchesResponsable && matchesAsignado && matchesEpica;
      })
      .sort((a, b) => {
        // Primero: tareas sin validar SEO van arriba
        if (a.validadoPorSEO !== b.validadoPorSEO) {
          return a.validadoPorSEO ? 1 : -1; // false (no validado) primero
        }
        // Segundo: ordenar por orden_sprint
        return (a.orden_sprint || 0) - (b.orden_sprint || 0);
      });
}, [allSprintTasks, activeSprint, searchTerm, globalSearchTerm, statusFilter, responsableFilter, asignadoFilter, epicaFilter]);

  // Tareas del sprint de PLANIFICACIÓN. `allPlanningTasks` sin filtrar (para el
  // modal de cierre y los conteos); `planningTasks` aplica los filtros globales
  // (mismos que el backlog: búsqueda global + estado/responsable/asignado/épica).
  const allPlanningTasks = useMemo(() => {
    if (!planningSprint) return [];
    return tasks.filter(task => task.sprintActual === planningSprint.id && task.estado !== 'borrador');
  }, [tasks, planningSprint]);

  const planningTasks = useMemo(() => {
    if (!planningSprint) return [];
    return allPlanningTasks
      .filter(task => {
        const matchesSearch = taskMatchesTerm(task, globalSearchTerm);
        const matchesStatus = statusFilter.length === 0 || statusFilter.includes(task.estado);
        const matchesResponsable = responsableFilter.length === 0 || responsableFilter.includes(task.responsable);
        const matchesAsignado = asignadoFilter.length === 0 || asignadoFilter.includes(task.assigned_to ? Number(task.assigned_to) : 0);
        const matchesEpica = epicaFilter.length === 0 || epicaFilter.includes(task.epica);
        return matchesSearch && matchesStatus && matchesResponsable && matchesAsignado && matchesEpica;
      })
      .sort((a, b) => (a.orden_sprint || 0) - (b.orden_sprint || 0));
  }, [allPlanningTasks, planningSprint, globalSearchTerm, statusFilter, responsableFilter, asignadoFilter, epicaFilter]);

  // Todas las tareas del backlog (sin filtrar) - para extraer opciones de filtros
  const allBacklogTasks = useMemo(() => {
    return tasks.filter(task => (!task.sprintActual || task.sprintActual === "0") && task.estado !== 'borrador');
  }, [tasks]);

  const backlogTasks = useMemo(() => {
    return allBacklogTasks
      .filter(task => {
        const matchesSearch = taskMatchesTerm(task, globalSearchTerm);

        const matchesStatus = statusFilter.length === 0 || statusFilter.includes(task.estado);
        const matchesResponsable = responsableFilter.length === 0 || responsableFilter.includes(task.responsable);
        const matchesAsignado = asignadoFilter.length === 0 || asignadoFilter.includes(task.assigned_to ? Number(task.assigned_to) : 0);
        const matchesEpica = epicaFilter.length === 0 || epicaFilter.includes(task.epica);

        return matchesSearch && matchesStatus && matchesResponsable && matchesAsignado && matchesEpica;
      })
      .sort((a, b) => (a.orden_global || 0) - (b.orden_global || 0));
}, [allBacklogTasks, globalSearchTerm, statusFilter, responsableFilter, asignadoFilter, epicaFilter]);

  // Opciones de responsables sobre TODAS las tareas (los filtros son globales)
  const responsables = useMemo(() => {
    return [...new Set(tasks.map(t => t.responsable).filter(Boolean))].sort();
  }, [tasks]);

  // Personas asignadas presentes en las tareas (para el filtro rápido estilo Jira).
  const asignados = useMemo(() => {
    const map = new Map();
    let hasUnassigned = false;
    tasks.forEach(t => {
      if (t.assigned_to) {
        const id = Number(t.assigned_to);
        if (!map.has(id)) map.set(id, { id, nombre: t.asignado_nombre, avatar: t.asignado_avatar });
      } else {
        hasUnassigned = true;
      }
    });
    const people = [...map.values()].sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));
    return { people, hasUnassigned };
  }, [tasks]);

  // Obtener épicas activas para filtros
  const activeEpics = useMemo(() => {
    return epics.filter(epic => epic.activo).sort((a, b) => (a.orden || 0) - (b.orden || 0));
  }, [epics]);

  // ============================================================================
  // MANEJADORES DE NAVEGACIÓN
  // ============================================================================

  const handleCreateTaskInSprint = () => {
    console.log('Creating task in active sprint');
    navigate('/task/create');
  };

  const handleCreateTaskInBacklog = () => {
    console.log('Creating new task in backlog');
    navigate('/task/create');
  };

  // Crear tarea directamente en el sprint de planificación (preselecciona su id
  // vía query param; TaskFormPage lo lee como sprint por defecto).
  const handleCreateTaskInPlanning = () => {
    if (!planningSprint) return;
    navigate(`/task/create?sprint=${planningSprint.id}`);
  };

  // ============================================================================
  // MANEJADORES DE MOVIMIENTO DE TAREAS - CORREGIDO
  // ============================================================================

  const handleMoveToSprint = async (taskId) => {
    if (!activeSprint) {
      showWarning('No hay sprint activo para mover la tarea');
      return;
    }
    const prev = tasks.find(t => t.id === taskId);
    const prevSprint = prev ? (prev.sprintActual ?? prev.sprint_actual ?? null) : null;

    try {
      console.log(`Moving task ${taskId} to active sprint ${activeSprint.id}`);
      const result = await moveTaskToSprint(taskId, activeSprint.id, token);

      if (!result.success) {
        showError('Error al mover la tarea al sprint: ' + result.error);
        return;
      }
      console.log('Task moved to sprint successfully');
      showUndo('Tarea movida al sprint', () => {
        moveTaskToSprint(taskId, prevSprint, token);
      });
    } catch (error) {
      console.error('Error moving task to sprint:', error);
      showError('Error al mover la tarea al sprint');
    }
  };

  // Mueve una tarea al sprint de PLANIFICACIÓN (para organizar el próximo mes).
  // Con undo, igual que mover al sprint/backlog. moveTaskToSprint acepta el id
  // de cualquier sprint del tenant.
  const handleMoveToPlanning = async (taskId) => {
    setMenuTask(null);
    if (!planningSprint) return;
    const prev = tasks.find(t => t.id === taskId);
    const prevSprint = prev ? (prev.sprintActual ?? prev.sprint_actual ?? null) : null;
    try {
      const result = await moveTaskToSprint(taskId, planningSprint.id, token);
      if (!result.success) {
        showError('Error al mover la tarea a planificación: ' + result.error);
        return;
      }
      showUndo(`Tarea añadida a "${planningSprint.nombre}"`, () => {
        moveTaskToSprint(taskId, prevSprint, token);
      });
    } catch (error) {
      console.error('Error moving task to planning:', error);
      showError('Error al mover la tarea a planificación');
    }
  };

  const handleMoveToBacklog = async (taskId) => {
    const prev = tasks.find(t => t.id === taskId);
    const prevSprint = prev ? (prev.sprintActual ?? prev.sprint_actual ?? null) : null;
    try {
      console.log(`Moving task ${taskId} back to backlog using moveTaskToSprint`);
      const result = await moveTaskToSprint(taskId, null, token);

      if (!result.success) {
        showError('Error al mover la tarea al backlog: ' + result.error);
        return;
      }
      console.log('Task moved to backlog successfully');
      showUndo('Tarea movida al backlog', () => {
        moveTaskToSprint(taskId, prevSprint, token);
      });
    } catch (error) {
      console.error('Error moving task to backlog:', error);
      showError('Error al mover la tarea al backlog: ' + error.message);
    }
  };

  // "Enviar a": mover al contenedor destino y colocar al inicio/final.
  // target: 'sprint' | 'backlog' ; position: 'start' | 'end'
  const handleSendTaskTo = async (task, target, position) => {
    setMenuTask(null);
    // Snapshot para deshacer: contenedor + posición previos.
    const prevSprint = task.sprintActual ?? task.sprint_actual ?? null;
    const prevOrdenSprint = task.orden_sprint;
    const prevOrdenGlobal = task.orden_global;
    try {
      const result = await sendTaskToPosition(task.id, target, position, token);
      if (!result.success) {
        showError('Error al mover la tarea: ' + (result.error || 'Error desconocido'));
        return;
      }
      showUndo('Tarea movida', () => {
        updateTask(task.id, {
          sprintActual: prevSprint,
          orden_sprint: prevOrdenSprint,
          orden_global: prevOrdenGlobal,
        }, token);
      });
    } catch (error) {
      showError('Error al mover la tarea: ' + error.message);
    }
  };

  // Alternar el bloqueo de una tarea (marcar / desmarcar "a la espera").
  const handleToggleBlock = async (task) => {
    setMenuTask(null);
    const nextBlocked = task.bloqueada ? 0 : 1;
    try {
      const result = await updateTask(task.id, { bloqueada: nextBlocked }, token);
      if (result.success) {
        showSuccess(nextBlocked ? 'Tarea marcada como bloqueada' : 'Bloqueo retirado');
      } else {
        showError('Error al actualizar el bloqueo: ' + (result.error || 'Error desconocido'));
      }
    } catch (error) {
      showError('Error al actualizar el bloqueo: ' + error.message);
    }
  };

  // Marca el bloqueo y salta al detalle con el editor de comentario enfocado
  // para explicar el motivo (reutiliza el timeline de comentarios existente).
  const handleBlockAndComment = async (task) => {
    setMenuTask(null);
    if (!task.bloqueada) {
      const result = await updateTask(task.id, { bloqueada: 1 }, token);
      if (result && result.success === false) {
        showError('Error al marcar el bloqueo: ' + (result.error || 'Error desconocido'));
        return;
      }
    }
    navigate(getTaskPath(task), { state: { focusComment: true } });
  };

  // ============================================================================
  // MANEJADORES DE DRAG AND DROP - CORREGIDO COMPLETAMENTE
  // ============================================================================

  // ✅ DRAG AND DROP DEL SPRINT - AHORA SÍ REORDENA
  const handleDragStart = (e, task) => {
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = 'move';
    console.log('Started dragging sprint task:', task.nombre);
  };

  const handleDragOver = (e, task) => {
    e.preventDefault();
    if (draggedTask && draggedTask.id !== task.id) {
      setDraggedOver(task);
    }
  };

  const handleDragLeave = () => {
    setDraggedOver(null);
  };

  const handleDrop = async (e, targetTask) => {
    e.preventDefault();

    if (!draggedTask || !targetTask || draggedTask.id === targetTask.id) {
      setDraggedTask(null);
      setDraggedOver(null);
      return;
    }

    // ❌ NO PERMITIR REORDENAR TAREAS VALIDADAS POR SEO
    if (draggedTask.validadoPorSEO || targetTask.validadoPorSEO) {
      console.log('❌ Cannot reorder SEO-validated tasks');
      showWarning('No se pueden reordenar tareas validadas por SEO');
      setDraggedTask(null);
      setDraggedOver(null);
      return;
    }

    console.log('🔄 REORDERING SPRINT TASKS:', draggedTask.nombre, '→', targetTask.nombre);

    try {
      // ✅ SOLO REORDENAR TAREAS NO VALIDADAS POR SEO
      const nonValidatedTasks = sprintTasks.filter(t => !t.validadoPorSEO);

      // Encontrar las posiciones dentro de las tareas NO validadas
      const draggedIndex = nonValidatedTasks.findIndex(t => t.id === draggedTask.id);
      const targetIndex = nonValidatedTasks.findIndex(t => t.id === targetTask.id);

      if (draggedIndex === -1 || targetIndex === -1) {
        console.error('Could not find task positions in non-validated tasks');
        return;
      }

      // Crear nuevo array con el reordenamiento
      const reorderedTasks = [...nonValidatedTasks];
      const [movedTask] = reorderedTasks.splice(draggedIndex, 1);
      reorderedTasks.splice(targetIndex, 0, movedTask);

      // Asignar nuevos órdenes SOLO a tareas no validadas: un único POST batch.
      const orders = reorderedTasks
        .map((task, index) => ({ id: task.id, orden: index + 1 }))
        .filter((o, index) => reorderedTasks[index].orden_sprint !== o.orden);

      const result = await reorderTasks('orden_sprint', orders, token);

      if (!result.success) {
        showError('Error al reordenar tareas: ' + (result.error || 'Error desconocido'));
      } else {
        showSuccess(`Tarea "${draggedTask.nombre}" reordenada exitosamente`);
      }

    } catch (error) {
      console.error('Error reordering sprint tasks:', error);
      showError('Error al reordenar tareas del sprint');
    } finally {
      setDraggedTask(null);
      setDraggedOver(null);
    }
  };

  // ✅ DRAG AND DROP DEL BACKLOG - AHORA SÍ REORDENA  
  const handleBacklogDragStart = (e, task) => {
    setBacklogDraggedTask(task);
    e.dataTransfer.effectAllowed = 'move';
    console.log('Started dragging backlog task:', task.nombre);
  };

  const handleBacklogDragOver = (e, task) => {
    e.preventDefault();
    if (backlogDraggedTask && backlogDraggedTask.id !== task.id) {
      setBacklogDraggedOver(task);
    }
  };

  const handleBacklogDragLeave = () => {
    setBacklogDraggedOver(null);
  };

  const handleBacklogDrop = async (e, targetTask) => {
    e.preventDefault();
    
    if (!backlogDraggedTask || !targetTask || backlogDraggedTask.id === targetTask.id) {
      setBacklogDraggedTask(null);
      setBacklogDraggedOver(null);
      return;
    }

    console.log('🔄 REORDERING BACKLOG TASKS:', backlogDraggedTask.nombre, '→', targetTask.nombre);

    try {
      const currentBacklogTasks = backlogTasks.slice();
      
      const draggedIndex = currentBacklogTasks.findIndex(t => t.id === backlogDraggedTask.id);
      const targetIndex = currentBacklogTasks.findIndex(t => t.id === targetTask.id);
      
      if (draggedIndex === -1 || targetIndex === -1) {
        console.error('Could not find task positions in backlog');
        return;
      }

      const reorderedTasks = [...currentBacklogTasks];
      const [movedTask] = reorderedTasks.splice(draggedIndex, 1);
      reorderedTasks.splice(targetIndex, 0, movedTask);

      // Usar orden_global para backlog: un único POST batch.
      const orders = reorderedTasks
        .map((task, index) => ({ id: task.id, orden: index + 1 }))
        .filter((o, index) => reorderedTasks[index].orden_global !== o.orden);

      const result = await reorderTasks('orden_global', orders, token);

      if (!result.success) {
        showError('Error al reordenar el backlog: ' + (result.error || 'Error desconocido'));
      } else {
        showSuccess(`Tarea "${backlogDraggedTask.nombre}" reordenada exitosamente en el backlog`);
      }

    } catch (error) {
      console.error('Error reordering backlog tasks:', error);
      showError('Error al reordenar tareas del backlog');
    } finally {
      setBacklogDraggedTask(null);
      setBacklogDraggedOver(null);
    }
  };
  // ============================================================================
  // NAVEGACIÓN Y ACCIONES
  // ============================================================================

  const handleTaskClick = (task) => {
    console.log('Task clicked:', task.nombre);
    
    if (onTaskSelect) {
      onTaskSelect(task);
    } else {
      console.log('View task details:', task.nombre);
    }
  };

  // ============================================================================
  // COMPONENTE DE TABLA REUTILIZABLE SIMPLIFICADO
  // ============================================================================

  // Handlers de fila con identidad ESTABLE: TaskRow es React.memo y no debe
  // re-renderizar porque el panel recree sus arrow functions. rowActionsRef se
  // reasigna en cada render (cierra sobre el estado fresco) y rowActions expone
  // wrappers creados una única vez que delegan en la versión actual.
  const rowActionsRef = useRef({});
  rowActionsRef.current = {
    taskClick: handleTaskClick,
    menuToggle: handleMenuToggle,
    statusMenuToggle: handleStatusMenuToggle,
    quickStatusChange: handleQuickStatusChange,
    epicMenuToggle: handleEpicMenuToggle,
    quickEpicChange: handleQuickEpicChange,
    responsableMenuToggle: handleResponsableMenuToggle,
    quickResponsableChange: handleQuickResponsableChange,
    copyLink: handleCopyLink,
    sendTaskTo: handleSendTaskTo,
    moveToPlanning: handleMoveToPlanning,
    toggleBlock: handleToggleBlock,
    blockAndComment: handleBlockAndComment,
    moveToSprint: (taskId) => { setMenuTask(null); handleMoveToSprint(taskId); },
    moveToBacklog: (taskId) => { setMenuTask(null); handleMoveToBacklog(taskId); },
    deleteTask: (task) => { setMenuTask(null); handleDeleteTask(task); },
    editTask: (taskId) => { setMenuTask(null); navigate(`/task/edit/${taskId}`); },
    closeMenus: () => {
      setMenuTask(null);
      setStatusMenu(null);
      setEpicMenu(null);
      setResponsableMenu(null);
    },
    assign: async (taskId, uid) => {
      const result = await updateTask(taskId, { assigned_to: uid }, token);
      if (result && result.success === false) {
        showError('Error al asignar: ' + (result.error || 'Error desconocido'));
      }
    },
    sprintDragStart: handleDragStart,
    sprintDragOver: handleDragOver,
    sprintDragLeave: handleDragLeave,
    sprintDrop: handleDrop,
    backlogDragStart: handleBacklogDragStart,
    backlogDragOver: handleBacklogDragOver,
    backlogDragLeave: handleBacklogDragLeave,
    backlogDrop: handleBacklogDrop,
  };
  const rowActions = useMemo(() => {
    const stable = {};
    for (const key of Object.keys(rowActionsRef.current)) {
      stable[key] = (...args) => rowActionsRef.current[key](...args);
    }
    return stable;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Opciones de grupo responsable del tenant (estables durante la sesión).
  const responsablesOptions = useMemo(() => appConfig.getResponsables(), []);

  // ============================================================================
  // TABLA: mapea TaskRow memoizadas (la fila solo se repinta si cambia lo suyo)
  // ============================================================================

  const TaskTable = ({
    tasks,
    emptyMessage,
    emptyAction,
    dragKind = 'none',
    draggedOver,
    showMoveButton = false
  }) => (
    <div className="sprint-table-container">
      <table className="sprint-table">
        {/* Sin cabecera de columnas: los anchos los fija colgroup */}
        <colgroup>
          <col className="col-drag" />
          <col className="col-id" />
          <col className="col-name" />
          <col className="col-epic" />
          <col className="col-priority" />
          <col className="col-status" />
          <col className="col-seo" />
          <col className="col-responsable" />
          <col className="col-asignado" />
          <col className="col-menu" />
        </colgroup>
        <tbody>
          {tasks.length === 0 ? (
            <tr>
              <td colSpan="10" className="empty-state-cell">
                <div className="empty-state">
                  {emptyMessage}
                  {emptyAction}
                </div>
              </td>
            </tr>
          ) : (
            <AnimatePresence initial={false}>
              {tasks.map((task) => (
                <TaskRow
                  key={task.id}
                  task={task}
                  epics={epics}
                  activeEpics={activeEpics}
                  responsables={responsablesOptions}
                  reduceMotion={reduceMotion}
                  isDragOver={draggedOver?.id === task.id}
                  isDeleting={deletingTaskId === task.id}
                  dragKind={dragKind}
                  showMoveButton={showMoveButton}
                  hasActiveSprint={!!activeSprint}
                  planningSprintId={planningSprint?.id || null}
                  isAdminFlag={isAdmin()}
                  menuState={menuTask?.id === task.id ? menuTask : null}
                  menuLayout={menuTask?.id === task.id ? menuLayout : null}
                  menuRef={menuRef}
                  statusMenuState={statusMenu?.id === task.id ? statusMenu : null}
                  epicMenuState={epicMenu?.id === task.id ? epicMenu : null}
                  responsableMenuState={responsableMenu?.id === task.id ? responsableMenu : null}
                  actions={rowActions}
                />
              ))}
            </AnimatePresence>
          )}
        </tbody>
      </table>
    </div>
  );

  // ============================================================================
  // RENDER PRINCIPAL
  // ============================================================================

  return (
    <div className="active-sprint-panel">
      
      {/* SPRINT ACTIVO */}
      <div className="sprint-container">
        
        {/* Controles del sprint */}
        <div className="sprint-task-controls">
          <div className="controls-left">
            <div className="search-container search-compact">
              <Search size={16} className="search-icon" />
              <input
                type="text"
                className="search-input"
                placeholder="Buscar en sprint"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                disabled={!activeSprint}
              />
            </div>

            {/* En móvil estos filtros forman la 2ª fila (tira horizontal) */}
            <div className="sprint-filter-strip">
            {/* Equipos responsables como avatares clicables (filtro global) */}
            <div className="team-filter-avatars">
              {responsables.map(r => (
                <button
                  key={r}
                  type="button"
                  className={`team-avatar ${responsableFilter.includes(r) ? 'active' : ''}`}
                  title={r}
                  onClick={() =>
                    setResponsableFilter(prev =>
                      prev.includes(r) ? prev.filter(v => v !== r) : [...prev, r]
                    )
                  }
                >
                  {getTeamInitials(r)}
                </button>
              ))}
            </div>

            {/* Separador + personas asignadas (filtro rápido estilo Jira) */}
            {(asignados.people.length > 0 || asignados.hasUnassigned) && (
              <>
                <span className="filter-sep" aria-hidden="true" />
                <div className="assignee-filter-avatars">
                  {asignados.hasUnassigned && (
                    <button
                      type="button"
                      className={`assignee-filter-chip ${asignadoFilter.includes(0) ? 'active' : ''}`}
                      title="Sin asignar"
                      onClick={() =>
                        setAsignadoFilter(prev =>
                          prev.includes(0) ? prev.filter(v => v !== 0) : [...prev, 0]
                        )
                      }
                    >
                      <span className="ap-unassigned" style={{ width: 26, height: 26 }}>
                        <User size={14} />
                      </span>
                    </button>
                  )}
                  {asignados.people.map(p => (
                    <button
                      key={p.id}
                      type="button"
                      className={`assignee-filter-chip ${asignadoFilter.includes(p.id) ? 'active' : ''}`}
                      title={p.nombre}
                      onClick={() =>
                        setAsignadoFilter(prev =>
                          prev.includes(p.id) ? prev.filter(v => v !== p.id) : [...prev, p.id]
                        )
                      }
                    >
                      <Avatar name={p.nombre} src={p.avatar} size={26} />
                    </button>
                  ))}
                </div>
              </>
            )}

            {/* Estados y épicas agrupados en un único botón Filtro */}
            <FilterMenu
              sections={[
                {
                  key: 'estados',
                  title: 'Estados',
                  options: [
                    { value: 'pendiente', label: 'Pendiente' },
                    { value: 'en-curso', label: 'En curso' },
                    { value: 'completada', label: 'Completada' },
                  ],
                  selected: statusFilter,
                  onChange: handleStatusFilterChange,
                },
                {
                  key: 'epicas',
                  title: 'Épicas',
                  options: activeEpics.map(e => ({ value: e.nombre, label: e.nombre })),
                  selected: epicaFilter,
                  onChange: setEpicaFilter,
                },
              ]}
            />

            {(searchTerm || statusFilter.length > 0 || responsableFilter.length > 0 || epicaFilter.length > 0) && (
              <button
                className="clear-filters-button"
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter([]);
                  setSprintStatusFilter([]);
                  setResponsableFilter([]);
                  setEpicaFilter([]);
                }}
                title="Limpiar filtros"
              >
                <RotateCcw size={16} />
                Limpiar
              </button>
            )}
            </div>

          </div>

          <div className="controls-right">
            <button
              className="history-button"
              onClick={() => navigate('/task/sprint-history')}
              title="Ver histórico de sprints"
              aria-label="Ver histórico de sprints"
            >
              <Archive size={16} />
            </button>
          </div>

        </div>

        {/* Tabla de tareas del sprint activo */}
        <div className="sprint-tasks-section">
          <div className="section-header-with-sprint">
            <div className="sprint-header-info">
              <h4 className="sprint-title-in-header">
                {activeSprint ? activeSprint.nombre : 'No hay sprint activo'}
              </h4>
              <div className="sprint-meta-inline">
                <span className="tasks-count">({sprintTasks.length} tareas)</span>
                {activeSprint ? (
                  <>
                    <span className="sprint-badge active">ACTIVO</span>
                    <span className="sprint-dates">
                      {formatDate(activeSprint.fecha_inicio)} - {formatDate(activeSprint.fecha_fin)}
                    </span>
                  </>
                ) : (
                  <span className="sprint-badge inactive">INACTIVO</span>
                )}
              </div>
            </div>

            <div className="sprint-header-actions">
              {/* Filtros de estado del sprint (visibles para todos) */}
              <SprintFilterRings />
              {isAdmin() && (activeSprint ? (
                <button
                  className="btn-close-sprint"
                  onClick={handleCloseSprint}
                  disabled={isLoading}
                  title="Terminar sprint actual"
                >
                  {isLoading ? 'Terminando...' : 'Terminar sprint'}
                </button>
              ) : (
                <button
                  className="create-task-button"
                  onClick={handleCreateSprint}
                  disabled={isLoading}
                  title="Crear nuevo sprint"
                >
                  <Plus size={14} />
                  {isLoading ? 'Creando...' : 'Crear sprint'}
                </button>
              ))}
            </div>
          </div>

          {TaskTable({
            tasks: sprintTasks,
            emptyMessage: activeSprint ? (
              searchTerm || globalSearchTerm || statusFilter.length > 0 || responsableFilter.length > 0 || epicaFilter.length > 0 ?
              "No se encontraron tareas en el sprint con los filtros aplicados" :
              "El sprint está vacío. Mueve tareas desde el backlog o crea nuevas."
            ) : (
              "No hay sprint activo. Crea un sprint para comenzar a trabajar."
            ),
            emptyAction: activeSprint ? (
              <button
                className="empty-action-button"
                onClick={handleCreateTaskInSprint}
              >
                <Plus size={16} />
                Crear primera tarea
              </button>
            ) : null,
            dragKind: 'sprint',
            draggedOver: draggedOver,
            showMoveButton: false,
          })}

          {activeSprint && (
            <button
              className="create-row-button"
              onClick={handleCreateTaskInSprint}
              title="Crear nueva tarea en el sprint"
            >
              <Plus size={15} />
              Crear
            </button>
          )}
        </div>
      </div>

      {/* SPRINT DE PLANIFICACIÓN (próximo sprint, no activo — se llena mientras el activo sigue en curso).
          Si no existe, el acceso para crearlo vive en la cabecera del backlog (abajo). */}
      {planningSprint && (
        <div className="planning-container">
          <div className="sprint-tasks-section">
            <div className="section-header-with-sprint">
              <div className="sprint-header-info">
                <h4 className="sprint-title-in-header">
                  {planningSprint.nombre}
                  <span className="plan-tag">Planificación</span>
                </h4>
                <div className="sprint-meta-inline">
                  <span className="tasks-count">({planningTasks.length} tareas)</span>
                </div>
              </div>
              {isAdmin() && (
                <div className="planning-header-actions">
                  <button
                    className="btn-plan-delete"
                    onClick={handleDeletePlanningSprint}
                    title="Eliminar el sprint de planificación"
                    aria-label="Eliminar sprint de planificación"
                  >
                    <Trash2 size={14} />
                  </button>
                  <button
                    className="btn-close-sprint"
                    onClick={handleStartSprint}
                    disabled={isLoading || !!activeSprint}
                    title={activeSprint
                      ? `Termina primero el sprint activo (${activeSprint.nombre}) para iniciar este`
                      : 'Iniciar este sprint'}
                  >
                    <Play size={14} />
                    Iniciar sprint
                  </button>
                </div>
              )}
            </div>

            {TaskTable({
              tasks: planningTasks,
              emptyMessage: 'Aún no hay tareas en la planificación. Añade lo que haréis el próximo sprint.',
              emptyAction: isAdmin() ? (
                <button className="empty-action-button" onClick={handleCreateTaskInPlanning}>
                  <Plus size={16} />
                  Añadir primera tarea
                </button>
              ) : null,
              dragKind: 'none',
              draggedOver: null,
              showMoveButton: true,
            })}

            {isAdmin() && (
              <button
                className="create-row-button"
                onClick={handleCreateTaskInPlanning}
                title="Añadir tarea a la planificación"
              >
                <Plus size={15} />
                Añadir tarea
              </button>
            )}
          </div>
        </div>
      )}

      {/* BACKLOG (con acceso a crear el sprint de planificación en su cabecera, si no existe) */}
      <div className="backlog-container">

        {/* Tabla de tareas del backlog */}
        <div className="sprint-tasks-section">
          <div className="section-header-with-sprint">
            <div className="sprint-header-info">
              <h4 className="sprint-title-in-header">
                Backlog
              </h4>
              <div className="sprint-meta-inline">
                <span className="tasks-count">({backlogTasks.length} tareas)</span>
                <span className="sprint-badge backlog">SIN SPRINT</span>
              </div>
            </div>

            {!planningSprint && isAdmin() && (
              <button
                className="btn-plan-header"
                onClick={handleCreatePlanningSprint}
                title="Crear un sprint de planificación para organizar el próximo mes"
              >
                <Plus size={14} />
                Sprint de planificación
              </button>
            )}
          </div>

          {TaskTable({
            tasks: backlogTasks,
            emptyMessage: globalSearchTerm || statusFilter.length > 0 || responsableFilter.length > 0 || epicaFilter.length > 0 ?
              "No se encontraron tareas en el backlog con los filtros aplicados" :
              "El backlog está vacío. Crea nuevas tareas aquí.",
            emptyAction: null,
            dragKind: 'backlog',
            draggedOver: backlogDraggedOver,
            showMoveButton: true,
          })}

          <button
            className="create-row-button"
            onClick={handleCreateTaskInBacklog}
            title="Crear nueva tarea en el backlog"
          >
            <Plus size={15} />
            Crear
          </button>
        </div>
      </div>

      {/* MODAL DE CREAR SPRINT (estado del formulario aislado en el propio
          componente → escribir no re-renderiza el panel animado, sin lag) */}
      {showCreateSprintModal && (
        <CreateSprintModal
          mode={createSprintMode}
          submitting={isLoading}
          onClose={() => setShowCreateSprintModal(false)}
          onSubmit={handleCreateSprintSubmit}
        />
      )}

      {/* ESTILOS COMPLETOS PARA REPS Y SEO */}
      <style>{`
        /* Columna SEO - Semáforo */
        .col-seo {
          width: 44px;
          min-width: 44px;
          text-align: center;
          padding: 8px 4px;
        }

        .seo-indicator {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;
        }

        .seo-circle {
          width: 9px;
          height: 9px;
          border-radius: 50%;
          transition: transform 0.15s ease;
        }

        .seo-indicator.seo-not-validated .seo-circle {
          background: #ef4444;
        }

        .seo-indicator.seo-validated .seo-circle {
          background: #10b981;
        }

        .seo-indicator:hover .seo-circle {
          transform: scale(1.25);
        }

        /* Spinner de carga para botón eliminar */
        .loading-spinner-small {
          width: 14px;
          height: 14px;
          border: 2px solid #f3f4f6;
          border-top: 2px solid #dc2626;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        /* Drag and Drop Visual Feedback */
        .task-row.drag-over {
          background-color: #dbeafe;
          border: 2px dashed #3b82f6;
          transition: all 0.2s ease;
        }

        .drag-handle {
          cursor: grab;
          opacity: 0.6;
          transition: opacity 0.2s ease;
        }

        .task-row:hover .drag-handle {
          opacity: 1;
        }

        .drag-handle:active {
          cursor: grabbing;
        }

        /* Tareas validadas por SEO - NO ARRASTRABLES */
        .task-row.seo-locked {
          cursor: default;
        }

        .task-row.seo-locked .drag-handle {
          cursor: not-allowed;
          opacity: 0.3;
          color: #9ca3af;
        }

        .task-row.seo-locked:hover .drag-handle {
          opacity: 0.3;
        }

        .drag-handle.drag-disabled {
          cursor: not-allowed !important;
          opacity: 0.3 !important;
          color: #9ca3af !important;
        }

        /* Sprint de planificación (próximo sprint, no activo) */
        .planning-container { margin-top: 18px; }
        .plan-tag {
          font-size: 10px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase;
          color: var(--accent-700); background: var(--accent-100); border: 1px solid var(--accent-300);
          padding: 2px 8px; border-radius: 6px; margin-left: 8px; vertical-align: middle;
        }
        .planning-header-actions { display: flex; align-items: center; gap: 6px; }
        .btn-plan-delete {
          display: inline-flex; align-items: center; justify-content: center;
          width: 28px; height: 28px; background: transparent; color: var(--ink-mute, #8b8894);
          border: 1px solid var(--border, #e8e8ec); border-radius: var(--radius-sm, 6px);
          cursor: pointer; transition: all 0.15s ease;
        }
        .btn-plan-delete:hover {
          background: var(--danger-soft, #fbeceb); color: var(--danger, #b3261e);
          border-color: var(--danger, #b3261e);
        }
        .btn-plan-header {
          display: inline-flex; align-items: center; gap: 6px;
          background: transparent; color: var(--ink-mute, #8b8894);
          border: 1px solid var(--border, #e8e8ec); border-radius: var(--radius, 10px);
          padding: 6px 12px; font-size: 12.5px; font-weight: 550; font-family: inherit; cursor: pointer;
          transition: all 0.15s ease;
        }
        .btn-plan-header:hover {
          background: var(--bg-subtle, #f9fafb); color: var(--ink-soft, #4a4854);
          border-color: var(--border-strong, #dcdce2);
        }

      `}</style>

      {showCloseSprintModal && activeSprint && (
        <CloseSprintModal
          sprint={activeSprint}
          sprintTasks={sprintTasks}
          planningSprint={planningSprint}
          onCancel={() => setShowCloseSprintModal(false)}
          onConfirm={handleConfirmCloseSprint}
        />
      )}

    </div>
  );
}

export default ActiveSprintPanel;